"use client";

import { useState } from "react";
import {
  type ChartStyle,
  ChartStyleFields,
  useChartStyle,
} from "@/components/tool-kit/charts/controls";
import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import { Notice, PillGroup } from "@/components/tool-kit/primitives";
import { WIDGET_H3 } from "@/components/tool-kit/styles";
import { TreeDiagram } from "@/components/tools/stats-distributions-more/tree-diagram";
import {
  BagFields,
  OutcomeTable,
  OutlineField,
  StagesFields,
} from "@/components/tools/stats-distributions-more/tree-fields";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { colorIndex } from "@/lib/tool-kit/charts/color";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  bagDraws,
  eventProbability,
  independentStages,
  listLeaves,
  MAX_LEAVES,
  parseOutline,
  type TreeError,
  type TreeLeaf,
  type TreeNode,
  type TreeResult,
} from "@/lib/tool-kit/stats-distributions-more/probability-tree";
import {
  fractionText,
  type Rational,
  ratValue,
} from "@/lib/tool-kit/stats-distributions-more/rational";
import { layoutTree } from "@/lib/tool-kit/stats-distributions-more/tree-layout";

/** English strings. A translated content file overrides these through `ui`. */
export const TREE_DIAGRAM_MAKER_STRINGS = {
  modeLabel: "How do you want to define the tree?",
  modeOutline: "Type the branches",
  modeStages: "Repeat the same stage",
  modeBag: "Draw from a bag",
  outlineLabel:
    "Branches — one per line, indented under the branch they follow",
  outlineHint:
    "Write each branch as a label and its probability: 0.3, 30% or 3/10. Leave one probability off in a group and it is worked out as the rest. If a label ends in a number, put a colon after it (Roll 1:).",
  outcomesLabel: "Outcomes of one stage — a label and a probability per line",
  outcomesHint: "Leave one probability off to make it the rest.",
  stagesLabel: "Number of stages",
  bagLabel: "What is in the bag — a label and a count per line",
  bagHint: "For example “Red 5”. Up to 8 different items.",
  drawsLabel: "Number of draws",
  replaceLabel: "Put each item back after drawing (with replacement)",
  displayLabel: "Show probabilities as",
  displayDecimals: "Decimals",
  displayFractions: "Fractions",
  startName: "the start",
  exampleOutline:
    "Rain 0.3\n  Late 0.6\n  On time 0.4\nDry 0.7\n  Late 0.2\n  On time 0.8",
  exampleStages: "Heads 0.5\nTails 0.5",
  exampleBag: "Red 5\nBlue 3",
  exampleTitle: "Weather and Being Late",
  outcomeColumn: "Outcome",
  calculationColumn: "Multiply along the branches",
  probabilityColumn: "Probability",
  includeColumn: "In the event",
  include: "Include in the event",
  clearSelection: "Clear the event",
  outcomesHeading: "Outcomes and the addition rule",
  eventNone:
    "Tick outcomes to build an event; their probabilities are added (the addition rule).",
  eventLine: "Probability of the ticked outcomes: {terms} = {total}",
  eventFraction: " ({fraction})",
  totalCheck: "All {count} outcomes together add to {total}, as they must.",
  labelsAndStyle: "Title and figure style",
  chartTitle: "Diagram title",
  colorLabel: "Highlight color",
  colorBlue: "Blue",
  colorOrange: "Orange",
  colorGreen: "Bluish green",
  colorVermillion: "Vermillion",
  colorSkyBlue: "Sky blue",
  colorPurple: "Reddish purple",
  colorYellow: "Yellow",
  colorBlack: "Black",
  apaStyle: "APA style figure",
  apaHint:
    "Takes the title out of the image and sets the figure number in bold and the title in italics above it — the APA 7 layout.",
  figureNumber: "Figure number",
  figureNote: "Note (optional)",
  figureLabel: "Figure {number}",
  noteLabel: "Note.",
  copyCaption: "Copy number and title",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  copyData: "Copy outcomes",
  copied: "Copied!",
  exportFailed:
    "This browser could not create the PNG. Try Download SVG, or another browser.",
  chartLabel: "Probability tree",
  chartDescription: "Probability tree with {count} outcomes. {outcomes}.",
  errEmpty: "Add at least one branch.",
  errLine: "Line {line} has no label.",
  errProbability:
    "Line {line}: the probability cannot be read. Write it as 0.3, 30% or 3/10 (between 0 and 1) — or end the label with a colon to leave it blank.",
  errSum:
    "The branches after “{group}” add to {sum}, not 1. Change a probability, or leave one blank to make it the rest.",
  errBlanks:
    "More than one branch after “{group}” has no probability. Leave at most one blank.",
  errTooMany:
    "This tree has {leaves} outcomes; the diagram handles up to {max}.",
  errStages: "Enter the number of stages as a whole number from 1 to 5.",
  errCounts:
    "Give each item a whole-number count of 1 or more, up to 8 different items.",
  errDraws:
    "Enter the number of draws as a whole number from 1 to 4 — and no more than the items in the bag when you do not put them back.",
} as const;

type Strings = typeof TREE_DIAGRAM_MAKER_STRINGS;
type Mode = "outline" | "stages" | "bag";
type Display = "decimals" | "fractions";
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const DEFAULT_EVENT = ["0/0", "1/0"];

function errorText(error: TreeError, strings: Strings, fmt: Format): string {
  switch (error.kind) {
    case "empty":
      return strings.errEmpty;
    case "line":
      return t(strings.errLine, { line: fmt(error.line) });
    case "probability":
      return t(strings.errProbability, { line: fmt(error.line) });
    case "sum":
      return t(strings.errSum, {
        group: error.group,
        sum: fmt(error.sum, { maximumFractionDigits: 6 }),
      });
    case "blanks":
      return t(strings.errBlanks, { group: error.group });
    case "tooMany":
      return t(strings.errTooMany, {
        leaves: fmt(error.leaves),
        max: fmt(MAX_LEAVES),
      });
    case "stages":
      return strings.errStages;
    case "counts":
      return strings.errCounts;
    default:
      return strings.errDraws;
  }
}

/** A probability as the reader asked to see it: a decimal, or a reduced fraction. */
function makeFormatter(display: Display, fmt: Format) {
  const decimal = (value: number) => fmt(value, { maximumFractionDigits: 4 });
  return {
    rational: (r: Rational) =>
      display === "fractions" ? fractionText(r) : decimal(ratValue(r)),
    leaf: (leaf: TreeLeaf) =>
      display === "fractions" && leaf.exact
        ? fractionText(leaf.exact)
        : decimal(leaf.value),
    decimal,
  };
}

interface Form {
  mode: Mode;
  outline: string;
  stageOutcomes: string;
  stages: string;
  bag: string;
  draws: string;
  replace: boolean;
}

function readTree(form: Form, startName: string): TreeResult {
  if (form.mode === "outline") {
    return parseOutline(form.outline, startName);
  }
  if (form.mode === "stages") {
    return independentStages(
      form.stageOutcomes,
      parseIntegerInput(form.stages) ?? 0,
      startName
    );
  }
  return bagDraws(form.bag, parseIntegerInput(form.draws) ?? 0, form.replace);
}

function TreeResults({
  roots,
  display,
  style,
  selected,
  setSelected,
  strings,
  fmt,
}: {
  roots: TreeNode[];
  display: Display;
  style: ChartStyle;
  selected: string[];
  setSelected: (update: (old: string[]) => string[]) => void;
  strings: Strings;
  fmt: Format;
}) {
  const format = makeFormatter(display, fmt);
  const leaves = listLeaves(roots);
  const chosen = new Set(selected);
  const event = eventProbability(leaves, chosen);
  const pathText = (leaf: TreeLeaf) => leaf.labels.join(", ");
  const layout = layoutTree({
    roots,
    leaves,
    title: style.apa ? "" : style.title,
    format: format.rational,
    outcomeText: (leaf) => `${pathText(leaf)}: ${format.leaf(leaf)}`,
    selected: chosen,
  });
  const total = leaves.reduce((sum, leaf) => sum + leaf.value, 0);
  const terms = leaves
    .filter((leaf) => chosen.has(leaf.id))
    .map((leaf) => format.leaf(leaf))
    .join(" + ");
  const exactTotal = event.exact ? fractionText(event.exact) : null;
  const eventTotal =
    display === "fractions" && exactTotal
      ? exactTotal
      : format.decimal(event.value);
  const fractionNote =
    display === "decimals" && exactTotal
      ? t(strings.eventFraction, { fraction: exactTotal })
      : "";
  const description = t(strings.chartDescription, {
    count: fmt(leaves.length),
    outcomes: leaves
      .map((leaf) => `${pathText(leaf)}: ${format.leaf(leaf)}`)
      .join("; "),
  });
  return (
    <>
      <ChartFigure
        apa={style.apa}
        data={toTsv([
          [strings.outcomeColumn, strings.probabilityColumn],
          ...leaves.map((leaf) => [
            pathText(leaf),
            Number(leaf.value.toPrecision(10)),
          ]),
        ])}
        figureNumber={style.figureNumber}
        filename={chartFilename(style.title, "probability-tree")}
        labels={strings}
        note={style.note}
        table={null}
        title={style.title}
      >
        <TreeDiagram
          color={CHART_COLORS[colorIndex(style.color)]}
          description={description}
          id="pt-diagram"
          label={style.title.trim() || strings.chartLabel}
          layout={layout}
        />
      </ChartFigure>
      <div aria-live="polite" className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.outcomesHeading}</h3>
        <OutcomeTable
          labels={strings}
          leaves={leaves}
          onClear={() => setSelected(() => [])}
          onToggle={(id, on) =>
            setSelected((old) => {
              const rest = old.filter((value) => value !== id);
              return on ? [...rest, id] : rest;
            })
          }
          selected={chosen}
          text={{
            branch: format.rational,
            product: format.leaf,
            path: pathText,
          }}
        />
        <Notice>
          {event.count === 0
            ? strings.eventNone
            : `${t(strings.eventLine, { terms, total: eventTotal })}${fractionNote}`}
        </Notice>
        <p className="text-muted-foreground text-sm">
          {t(strings.totalCheck, {
            count: fmt(leaves.length),
            total: format.decimal(total),
          })}
        </p>
      </div>
    </>
  );
}

export default function TreeDiagramMakerWidget() {
  const strings = useToolUi(TREE_DIAGRAM_MAKER_STRINGS);
  const fmt = useToolNumber();
  const [form, setForm] = useState<Form>({
    mode: "outline",
    outline: strings.exampleOutline,
    stageOutcomes: strings.exampleStages,
    stages: "3",
    bag: strings.exampleBag,
    draws: "2",
    replace: false,
  });
  const [display, setDisplay] = useState<Display>("decimals");
  const [selected, setSelected] = useState<string[]>(DEFAULT_EVENT);
  const [style, update] = useChartStyle({
    title: strings.exampleTitle,
    xTitle: "",
    yTitle: "",
    color: "blue",
    valueLabels: false,
    apa: false,
    figureNumber: "1",
    note: "",
  });
  const edit = (patch: Partial<Form>) =>
    setForm((current) => ({ ...current, ...patch }));
  const result = readTree(form, strings.startName);
  const choose = (mode: Mode) => {
    edit({ mode });
    setSelected(mode === "outline" ? DEFAULT_EVENT : []);
  };

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="pt-mode"
        onChange={choose}
        options={[
          { value: "outline", label: strings.modeOutline },
          { value: "stages", label: strings.modeStages },
          { value: "bag", label: strings.modeBag },
        ]}
        value={form.mode}
      />
      {form.mode === "outline" ? (
        <OutlineField
          labels={strings}
          onChange={(outline) => edit({ outline })}
          value={form.outline}
        />
      ) : null}
      {form.mode === "stages" ? (
        <StagesFields
          labels={strings}
          onOutcomes={(stageOutcomes) => edit({ stageOutcomes })}
          onStages={(stages) => edit({ stages })}
          outcomes={form.stageOutcomes}
          stages={form.stages}
        />
      ) : null}
      {form.mode === "bag" ? (
        <BagFields
          draws={form.draws}
          items={form.bag}
          labels={strings}
          onDraws={(draws) => edit({ draws })}
          onItems={(bag) => edit({ bag })}
          onReplace={(replace) => edit({ replace })}
          replace={form.replace}
        />
      ) : null}
      <PillGroup
        label={strings.displayLabel}
        name="pt-display"
        onChange={setDisplay}
        options={[
          { value: "decimals", label: strings.displayDecimals },
          { value: "fractions", label: strings.displayFractions },
        ]}
        value={display}
      />
      <div aria-live="polite" className="space-y-6">
        {result.ok ? (
          <TreeResults
            display={display}
            fmt={fmt}
            roots={result.roots}
            selected={selected}
            setSelected={setSelected}
            strings={strings}
            style={style}
          />
        ) : (
          <Notice>{errorText(result.error, strings, fmt)}</Notice>
        )}
      </div>
      <ChartStyleFields
        colorLabels={strings}
        idPrefix="pt"
        labels={strings}
        style={style}
        update={update}
      />
    </div>
  );
}
